import type { GuardianPlayerLevelDetailPinKey } from '@/features/guardian-player/types';

export type GuardianPlayerSkillContent = {
  whatItTests: string;
  criteria: string[];
};

type SkillContentByPin = Partial<
  Record<GuardianPlayerLevelDetailPinKey, GuardianPlayerSkillContent>
>;

export const LEVEL_DETAIL_SKILL_CONTENT: Record<number, SkillContentByPin> = {
  1: {
    putting: {
      whatItTests:
        "Evaluates the player's ability to set up correctly and make a smooth putting stroke using proper grip and pendulum motion. Evaluates a beginner player's ability to perform basic forms and skills. Once a player passes Level 1 Putting they will know what a putter is, how to hold it and how to execute a putting stroke.",
      criteria: [
        'Selects the correct club (putter)',
        'Thumbs on top of the flat part of the grip',
        'Arms and putter form the letter "Y" at setup',
        'Ball positioned slightly forward in stance',
        'Swing the "Y" the same distance back and through',
      ],
    },
    aroundTheGreen: {
      whatItTests:
        "Evaluates the player's ability to chip the ball into the air and land it on the green from just off the putting surface using proper grip, setup, and a controlled swing. Evaluates a beginner player's ability to perform basic forms and skills. When a player passes Level 1 Around The Green they will know what a Yard Club is, how to hold it and to execute an Around The Green shot.",
      criteria: [
        'Selects the correct club (Yard Club or PW with Training Grip)',
        'Uses 10-finger "Hotdog in the Bun" grip',
        '"Y" setup with narrow stance and ball slightly forward',
        'Swings the "Y" low and wide, rotating hips and arms as a unit',
        'Club brushes the grass through impact',
      ],
    },
    fullSwing: {
      whatItTests:
        'Evaluates the player\'s ability to execute a complete full swing - from setup through the backswing "L" position to brushing the grass at impact - using proper grip and club selection. Evaluates a beginner player\'s ability to perform basic forms and skills. When the player passes Level 1 Full Swing they will know what a driver and iron is, how to hold it, and how to hit a full swing shot.',
      criteria: [
        'Selects the correct club (Yard Club or Driver with Training Grip)',
        'Uses 10-finger "Hotdog in the Bun" grip',
        '"Y" setup with ball slightly forward of middle',
        'Swings the "Y" low and wide, then up to a full "L"',
        'Releases the "L" on the downswing and brushes the grass',
      ],
    },
    scoring: {
      whatItTests:
        "Evaluates the player's ability to combine all skills - driving, approach shots, and putting - to complete 3 consecutive holes at a beginner-appropriate distance. Evaluates the player's ability to navigate the golf course from 50 yards. Once completed the player has proven they can score a 18 on 3 holes from 50 yards.",
      criteria: [
        'Plays 3 consecutive holes at 50 yards per hole',
        'Uses Driver (w/ Training Grip), Yard Club or PW (w/ Training Grip), and Putter',
        'Demonstrates proper Forms throughout the round',
        'Manages pace and course etiquette during play',
        'Scores 18 or better over 3 holes',
      ],
    },
    knowledge: {
      whatItTests:
        "Evaluates the player's understanding of basic golf etiquette, rules, terminology, and equipment identification - the foundational knowledge needed to play safely and respectfully. Evaluates the player's understanding of basic golf Etiquette, Rules, and Terminology.",
      criteria: [
        'Understands basic safety and etiquette on the course (standing position, cart behavior, ready golf)',
        'Knows fundamental rules (moving ball, flag in/out, missed stroke counts)',
        'Identifies all club types (driver, irons, wedges, putter) and club parts (face, clubhead, shaft, grip)',
        'Recognizes basic golf terminology: bag items, course features, and scoring terms (birdie, par, bogey)',
      ],
    },
  },
  2: {
    putting: {
      whatItTests:
        "Evaluates the player's ability to control putting distance by varying stroke length while maintaining a still body and consistent stroke tempo. Builds on what was learned in Level 1. At the completion of Level 2 the player will be able to control the length of the stroke and be able to stick the finish.",
      criteria: [
        'All Level 1 Forms maintained',
        'Practices long, short, and middle stroke lengths',
        'Stays very still during stroke - no head or lower body movement',
        'Sticks the finish position',
        'Demonstrates distance control across varied putt lengths',
      ],
    },
    aroundTheGreen: {
      whatItTests:
        "Evaluates the player's ability to control chipping distance by varying the length of the backswing and follow-through, finishing in a balanced position with weight on the front foot. Builds on what was learned in Level 1. At the completion of Level 2 the player will be able to control the length of the swing and be able to stick the finish.",
      criteria: [
        'All Level 1 Forms maintained',
        'Practices big, little, and middle swings for distance control',
        'Backswing length matches follow-through length',
        'Sticks the finish with weight on front foot',
        'Ball played from the ground (no tee)',
      ],
    },
    fullSwing: {
      whatItTests:
        "Evaluates the player's ability to execute a complete and balanced full swing with good tempo, finishing with weight on the front foot and showing the sole of the back shoe.",
      criteria: [
        'All Level 1 Forms maintained',
        'Practices swing at varying speeds (fast, slow, good tempo)',
        'Maintains good Forms throughout all tempos',
        'Sticks the finish with weight on front foot',
        'Shows bottom of back shoe at finish ("Show Some Shoe")',
      ],
    },
    scoring: {
      whatItTests:
        "Evaluates the player's ability to play a short round from increased distance, demonstrating improved shot selection, distance control, and course management.",
      criteria: [
        'Plays 3 consecutive holes at 100 yards per hole',
        'Uses Driver (w/ Training Grip), Yard Club or PW (w/ Training Grip), and Putter',
        'Applies distance control skills learned in Level 2',
        'Shows balanced finishes and tempo throughout the round',
        'Scores 18 or better over 3 holes',
      ],
    },
    knowledge: {
      whatItTests:
        "Evaluates the player's expanded understanding of golf etiquette, rules, and terminology - including scorecard management, course features, and parts of the clubhead.",
      criteria: [
        'Understands scorecard management and respect for fellow players (score counting, silence, turn order)',
        'Knows intermediate rules (tee placement, lightning safety, cart path relief, bunker restrictions)',
        'Identifies parts of the clubhead (sole, loft, grooves, heel, toe)',
        'Recognizes expanded terminology: course areas (fairway, rough, bunker) and scoring terms (eagle, double bogey, tee time)',
      ],
    },
  },
  3: {
    putting: {
      whatItTests:
        "Evaluates the player's ability to aim and align the putter correctly, with eyes over the ball and body parallel to the target line, while maintaining all previous putting fundamentals.",
      criteria: [
        'All Level 1-2 Forms maintained',
        'Eyes positioned directly over the ball',
        'Putter face aimed at the target',
        'Body alignment (knees, hips, shoulders) parallel to target line',
        'Consistent aim and alignment over 5+ putts',
      ],
    },
    aroundTheGreen: {
      whatItTests:
        "Evaluates the player's ability to aim and align the body correctly toward the target while executing a controlled chip shot with proper technique and consistency.",
      criteria: [
        'All Level 1-2 Forms maintained',
        'Body aligned parallel to the target line (train track visualization)',
        'Clubface aimed at the target',
        'Consistent direction and landing zone over multiple attempts',
        'Ball played from the ground with a PW',
      ],
    },
    fullSwing: {
      whatItTests:
        "Evaluates the player's ability to execute a complete and balanced full swing while maintaining proper posture, rhythm, and club control throughout the motion, with correct aim and body alignment toward the target.",
      criteria: [
        'All Level 1-2 Forms maintained',
        'Body aligned parallel to the target line',
        'Clubface aimed at the target',
        'Balanced finish position with full weight transfer',
        'Consistent over 5+ swings',
      ],
    },
    scoring: {
      whatItTests:
        "Evaluates the player's ability to play from standard beginner distances, applying aim, alignment, and all fundamental skills in an on-course setting.",
      criteria: [
        'Plays 3 consecutive holes at 150 yards per hole (or RightStart Tee)',
        'Uses Driver, PW, and Putter',
        'Demonstrates aim and alignment toward targets',
        'Applies all Level 1-3 Forms during play',
        'Scores 18 or better over 3 holes',
      ],
    },
    knowledge: {
      whatItTests:
        "Evaluates the player's deeper understanding of golf etiquette, rules, and equipment - including self-respect on the course, relief procedures, and proper club fitting.",
      criteria: [
        'Understands self-respect on the course (no quitting, staying positive, best effort every shot)',
        'Knows advanced relief rules (obstructions, loose impediments, re-dropping, temporary water, penalty areas)',
        'Understands proper club fitting - clubs should match player height; knows 14-club limit',
        'Recognizes advanced terminology: course features (penalty area, fringe/collar) and playing terms (divot, putt out)',
      ],
    },
  },
  4: {
    putting: {
      whatItTests:
        "Evaluates the player's ability to read the green, identify a target on breaking putts, and execute putts from five distances with improved accuracy and distance control.",
      criteria: [
        'Reads the green: sees and feels the slope by positioning behind the ball',
        'Finds the target - locates the spot where the ball should be aimed',
        'All Level 1-3 Forms maintained (Thumbs on Top, "Y" setup, eyes over ball, aim at target)',
        'Swing the "Y" with backswing = follow-through',
        'Stick the finish and be still',
        'Demonstrates green-reading before each putt',
      ],
    },
    aroundTheGreen: {
      whatItTests:
        "Evaluates the player's ability to read the lie of the ball and choose a target before executing a chip shot over a hurdle, landing and keeping the ball on the green with high consistency.",
      criteria: [
        'Sees and feels the lie: evaluates good/bad spot, tall/short grass, slope',
        'Finds the target: determines if green is uphill/downhill/flat, locates aim spot',
        'All Level 1-3 Forms maintained (Hotdog in the Bun, "Y" setup, ball slightly forward, swing "Y" low and wide, brush the grass, aim at target)',
        'Backswing = follow-through',
        'Stick the finish and be still',
      ],
    },
    fullSwing: {
      whatItTests:
        "Evaluates the player's ability to read the lie, choose a target, and execute a full swing with proper form - achieving target swing speed (driver) and carry distance (iron) while hitting between alignment sticks for accuracy.",
      criteria: [
        'Sees and feels the lie: evaluates ball position, grass type, slope',
        'Finds the target: stands behind ball, locates landing spot, finds target line on the ground',
        'All Level 1-3 Forms maintained (Hotdog in the Bun, "Y" setup, swing "Y" to full "L", release the "L", brush the grass)',
        'Aim at the target',
        'Stick the finish, show some shoe',
        'Shot must travel between and over alignment sticks (10′ apart, 15′ from player)',
      ],
    },
    scoring: {
      whatItTests:
        "Evaluates the player's ability to combine all skills to complete 6 consecutive holes from RightStart Tees, demonstrating course management and scoring ability at intermediate distances.",
      criteria: [
        'Plays 6 consecutive holes from RightStart Tees (or 150 yards/hole)',
        'Uses clubs of choice',
        'Applies proper Forms and pre-shot routine on every shot',
        'Demonstrates course management and shot selection',
        'Recommended: Score max of 10 per hole',
        'Can be attested by a coach, parent, or another player',
      ],
    },
    knowledge: {
      whatItTests:
        "Evaluates the player's understanding of respect for the game, advanced rules scenarios, new terminology, and golf ball characteristics.",
      criteria: [
        'Demonstrates respect for the game (golf history, clubhouse manners, dress code, punctuality)',
        'Knows rules for unplayable lies, wrong ball penalties, flagstick removal, and ball marking on the green',
        'Understands golf ball properties (spin, dimple design, distance based on swing speed)',
        'Recognizes advanced terminology: waste area, drop zone, out-of-bounds, ground under repair',
      ],
    },
  },
  5: {
    putting: {
      whatItTests:
        'Evaluates the player\'s ability to execute a complete putting routine in the "Get Ready, Get Set, Go Play" sequence - reading the green, setting up with proper forms, and executing with consistency under tighter scoring standards.',
      criteria: [
        'Executes the full "Get Ready, Get Set, Go Play" pre-putt routine in correct sequence',
        'Reads the green by seeing and feeling the slope, then selects a target before addressing the ball',
        'Sets up with proper grip (thumbs on top), "Y" position, eyes over the ball, and practices stroke length',
        'Swings the "Y" with backswing = follow-through, sticks the finish and stays still',
        'Demonstrates all 9 putting forms consistently across every putt',
      ],
    },
    aroundTheGreen: {
      whatItTests:
        'Evaluates the player\'s ability to execute a complete chipping routine in the "Get Ready, Get Set, Go Play" sequence - reading the lie, selecting a club, and executing with near-perfect consistency over a hurdle.',
      criteria: [
        'Executes the full "Get Ready, Get Set, Go Play" pre-shot routine in correct sequence',
        'Reads the lie and green conditions, selects the right club, and identifies a landing target before addressing the ball',
        'Sets up with "Hotdog in the Bun" grip, "Y" position with ball slightly forward, practices the swing, and aims at target',
        'Swings the "Y" low and wide with matching backswing and follow-through, brushes the grass, sticks the finish and stays still',
        'Demonstrates all 11 chipping forms consistently across every shot',
      ],
    },
    fullSwing: {
      whatItTests:
        'Evaluates the player\'s ability to execute a complete full swing routine in the "Get Ready, Get Set, Go Play" sequence - with perfect consistency on swing speed, carry distance, and accuracy through alignment sticks.',
      criteria: [
        'Executes the full "Get Ready, Get Set, Go Play" pre-shot routine in correct sequence',
        'Reads the lie and conditions, selects the right club, and identifies a target line from behind the ball',
        'Sets up with "Hotdog in the Bun" grip, "Y" position with ball slightly forward, practices the swing, and aims at target',
        'Swings the "Y" to full "L", releases the "L" and brushes the grass, sticks the finish and shows some shoe',
        'Demonstrates all 10 full swing forms consistently; shot must travel between and over alignment sticks',
      ],
    },
    scoring: {
      whatItTests:
        'Evaluates the player\'s ability to complete a full 9-hole round from RightStart Tees, applying the complete "Get Ready, Get Set, Go Play" routine with consistent scoring across all holes.',
      criteria: [
        'Plays 9 consecutive holes from RightStart Tees (or 150 yards/hole)',
        'Uses clubs of choice',
        'Demonstrates "Get Ready, Get Set, Go Play" routine on every shot',
        'Shows course management, composure, and etiquette',
        'Recommended: Score max of 10 per hole',
        'Can be attested by a coach, parent, or another player',
        'Completing Level 5 earns the Gold Star Player Medal',
      ],
    },
    knowledge: {
      whatItTests:
        "Evaluates the player's understanding of pace of play, advanced etiquette, rules involving ball markers and drops, new terminology, and club",
      criteria: [
        'Understands pace of play etiquette (45-second rule, productive walk-up, quiet during pre-shot routines)',
        'Knows rules for ball marker movement, waste area practice swings, knee-height drops, and ground under repair relief',
        'Understands club properties (weight affects swing speed, length varies, loft varies, cleaning importance)',
        'Recognizes advanced terminology: fairway metals, hybrid, range finder, immovable obstruction, embedded ball',
        'Can describe respect in 4 areas: course, others, yourself, the game',
      ],
    },
  },
};
